import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, Trash2, Loader2 } from "lucide-react";

// Account management for customers and cleaners: change password, close account.
export const AccountSecurity = () => {
  const { logout } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [del, setDel] = useState({ password: "", confirm: "" });
  const [delBusy, setDelBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) { toast.error("Your new password must be at least 6 characters."); return; }
    if (pw.new_password !== pw.confirm) { toast.error("The new passwords don't match."); return; }
    setPwBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: pw.current_password, new_password: pw.new_password,
      });
      toast.success(data.message);
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };

  const deleteAccount = async () => {
    setDelBusy(true);
    try {
      await api.post("/auth/delete-account", { password: del.password, confirm: del.confirm.trim() });
      toast.success("Your account has been closed.");
      await logout();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDelBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="account-security">
      <div className="bg-card rounded-2xl border border-primary/5 p-6">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-accent" />
          <h3 className="font-display font-semibold">Change password</h3>
        </div>
        <form onSubmit={changePassword} className="mt-4 space-y-3 max-w-sm">
          <input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
            placeholder="Current password" className="as-input" data-testid="current-password-input" />
          <input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            placeholder="New password (min 6 characters)" className="as-input" data-testid="new-password-input" />
          <input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            placeholder="Confirm new password" className="as-input" data-testid="confirm-password-input" />
          <button type="submit" disabled={pwBusy}
            className="px-5 py-2.5 rounded-full bg-accent text-white font-medium text-sm hover:bg-accent/90 transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="change-password-btn">
            {pwBusy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
          </button>
          <p className="text-xs text-muted-foreground">
            Your other devices are signed out when your password changes.
          </p>
        </form>
      </div>

      <div className="bg-card rounded-2xl border border-destructive/30 p-6">
        <div className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-destructive" />
          <h3 className="font-display font-semibold">Account management</h3>
        </div>
        {!openDelete ? (
          <>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Closing your account signs you out for good, disables your login and removes your
              profile from My Clean Connect™.
            </p>
            <button onClick={() => setOpenDelete(true)}
              className="mt-4 px-5 py-2.5 rounded-full border border-destructive/40 text-destructive font-medium text-sm hover:bg-destructive/5 transition-colors"
              data-testid="open-delete-account">Delete account</button>
          </>
        ) : (
          <div className="mt-3 max-w-xl" data-testid="delete-account-panel">
            <p className="text-sm font-semibold text-destructive">This cannot be undone.</p>
            <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc pl-5">
              <li>You won't be able to sign in with this account again.</li>
              <li>Your public profile is removed and, if you're a cleaner, you disappear from customer search straight away.</li>
              <li>Your future availability is removed and you can't take new bookings.</li>
              <li>You're signed out on every device.</li>
              <li>Completed bookings, payments and invoices are kept only for as long as we must
                  retain them for legal, accounting, fraud-prevention and dispute-resolution reasons —
                  they are not used to keep your profile visible.</li>
            </ul>
            <div className="mt-4 space-y-3 max-w-sm">
              <input type="password" value={del.password} onChange={(e) => setDel({ ...del, password: e.target.value })}
                placeholder="Your password" className="as-input" data-testid="delete-password-input" />
              <input value={del.confirm} onChange={(e) => setDel({ ...del, confirm: e.target.value })}
                placeholder="Type DELETE to confirm" className="as-input" data-testid="delete-confirm-input" />
              <div className="flex gap-3">
                <button onClick={deleteAccount} disabled={delBusy || del.confirm.trim() !== "DELETE"}
                  className="px-5 py-2.5 rounded-full bg-destructive text-white font-medium text-sm hover:bg-destructive/90 transition-colors inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid="confirm-delete-account">
                  {delBusy && <Loader2 className="h-4 w-4 animate-spin" />} Permanently close my account
                </button>
                <button onClick={() => setOpenDelete(false)}
                  className="px-5 py-2.5 rounded-full border border-border font-medium text-sm hover:bg-secondary transition-colors"
                  data-testid="cancel-delete-account">Keep my account</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`.as-input{width:100%;padding:0.65rem 0.85rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none;font-size:0.875rem}.as-input:focus{border-color:hsl(var(--accent))}`}</style>
    </div>
  );
};

export default AccountSecurity;
